/* FOM · BRAIN VIEW — a leitura radial do conhecimento de um contexto (Fase 1Y).

   O QUE ISTO E: outra LENTE sobre os MESMOS dados do mapa. Centro = o no em
   foco; orbita superior = a estrutura diretamente ligada (filhos e pontas de
   relacao); hemisferio inferior = o nucleo de conhecimento — cada disco e UM
   conhecimento real, cada anel tracejado e UMA lacuna real, cada anel duplo e
   UMA investigacao real. NAO existe particula decorativa: se o dado nao
   existe, o espaco fica honestamente vazio ("nenhum conhecimento consolidado
   ainda"). Nenhuma conexao e desenhada sem vinculo real.

   O que cada pergunta encontra aqui:
     "o que este contexto sabe?"        discos, com o nivel epistemico ESCRITO
     "o que sustenta?"                  clique -> painel Conhecimento (fonte)
     "o que falta descobrir?"           aneis tracejados (lacunas)
     "onde ha pergunta aberta?"         aneis de investigacao com codigo
     "documentado × observado?"         E2 × E1, no rotulo e na legenda

   Acessibilidade: nada depende so de cor — todo estado tem TEXTO (rotulo
   ROTULO_ST, legenda com contagens), todo elemento clicavel e <g role=button>
   com aria-label e tabindex. */
import type { FomNode, Relation, NodeType, InvestigationResumo } from '../core/types.ts';
import { COR_ST, ROTULO_ST, ROTULO_TIPO } from '../core/types.ts';
import { compararIrmaos } from '../core/layout.ts';
import { ehProduto, derivarCadeiaDoProduto, hospedeiroDaCadeia } from '../dominio/dimensoes.ts';
import { guiaDe, FONTES_GUIA, MARCA_AUSENTE } from '../dominio/guia-operacional.ts';
void MARCA_AUSENTE;

const NS = 'http://www.w3.org/2000/svg';

function el<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}

function texto(conteudo: string, attrs: Record<string, string | number>): SVGTextElement {
  const t = el('text', attrs);
  t.textContent = conteudo;
  return t;
}

/* distribui n itens num arco [a0..a1] (graus), raio r */
function arco(n: number, a0: number, a1: number, r: number): { x: number; y: number }[] {
  const saida: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const a = ((a0 + (a1 - a0) * t) * Math.PI) / 180;
    saida.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return saida;
}

/* rotulo de ZONA espacial — so aparece quando a zona tem conteudo real */
function rotuloZona(g: SVGGElement, txt: string, x: number, y: number): void {
  const t = texto(txt, { class: 'cerebro-zona', x, y, 'text-anchor': 'middle' });
  g.appendChild(t);
}

/* phyllotaxis (girassol): empacota n itens num aglomerado organico — a
   MASSA de conhecimento das referencias, com cada particula sendo um item
   REAL. O raio do aglomerado cresce com a raiz do total: a assimetria dos
   dados vira assimetria visual. */
function aglomerado(n: number, passo = 13): { x: number; y: number; raio: number } {
  void n;
  return { x: 0, y: 0, raio: passo * Math.sqrt(Math.max(n, 1)) + 14 };
}
function pontoAglomerado(i: number, passo = 13): { x: number; y: number } {
  const r = passo * Math.sqrt(i + 0.5);
  const a = i * 2.39996; /* angulo aureo */
  return { x: Math.cos(a) * r, y: Math.sin(a) * r };
}

export type CerebroOpcoes = {
  aoSelecionar: (externalId: string) => void;
  aoAbrirAba: (aba: 'conhecimento' | 'lacunas' | 'investigacoes') => void;
  aoVoltarAoMapa: () => void;
  /* divergencia documentado × observado vira PERGUNTA no mecanismo
     existente — nunca julgamento (Fase 1AC, item 8) */
  aoInvestigarLacuna?: (no: FomNode, pergunta: string) => void;
};

export function montarCerebro(host: HTMLElement, opcoes: CerebroOpcoes) {
  const raiz = document.createElement('div');
  raiz.className = 'cerebro';
  raiz.hidden = true;
  host.appendChild(raiz);

  function render(no: FomNode, nosEntrada: FomNode[], relacoesEntrada: Relation[], invs: InvestigationResumo[]): void {
    raiz.textContent = '';
    /* ORDEM DETERMINISTICA (FOM-ARQ-0049): a cena e derivada de LISTAS, e
       toda lista aqui nasce de filter/find sobre nos e relacoes. Normalizar
       UMA vez na entrada — nos por (parent, fluxo, ordinal, external_id) e
       relacoes por (ordinal, id) — garante que a posicao no array vindo da
       API ou do store nunca decide nada: um no recem-criado (anexado ao fim)
       e um F5 (ordenado pelo banco) produzem a mesma cena. Copias: a entrada
       nao e mutada. */
    const nos = nosEntrada.slice().sort((a, b) =>
      (a.parent_external_id ?? '').localeCompare(b.parent_external_id ?? '') || compararIrmaos(a, b));
    const relacoes = relacoesEntrada.slice().sort((r1, r2) => (r1.ordinal - r2.ordinal) || r1.id.localeCompare(r2.id));

    /* ------------------------------------------------- topo: contexto ---- */
    const topo = document.createElement('div');
    topo.className = 'cerebro-topo';
    const voltar = document.createElement('button');
    voltar.className = 'cerebro-voltar';
    voltar.textContent = '‹ mapa';
    voltar.title = 'voltar a visao estrutural (Esc)';
    voltar.addEventListener('click', () => opcoes.aoVoltarAoMapa());
    topo.appendChild(voltar);
    const nome = document.createElement('span');
    nome.className = 'cerebro-contexto';
    nome.textContent = no.name;
    topo.appendChild(nome);
    const modo = document.createElement('span');
    modo.className = 'cerebro-modo';
    modo.textContent = 'conhecimento deste contexto';
    topo.appendChild(modo);
    /* AUDITORIA SOB DEMANDA (1Z §18-19): o "prove isso" do fiscal, sem
       transformar a tela executiva em tela de auditoria. */
    const btnEvid = document.createElement('button');
    btnEvid.className = 'cerebro-evid-botao';
    btnEvid.textContent = 'Evidencias';
    btnEvid.setAttribute('aria-pressed', 'false');
    btnEvid.title = 'camada probatoria deste contexto (documentos, evidencias, proveniencia)';
    topo.appendChild(btnEvid);
    raiz.appendChild(topo);


    /* ---------------------------------------------------------- cena ----- */
    const svg = el('svg', {
      class: 'cerebro-svg', viewBox: '-380 -300 760 620',
      preserveAspectRatio: 'xMidYMid meet', role: 'img',
      'aria-label': `Rede de conhecimento de ${no.name}`,
    });

    /* guias radiais — estrutura do espaco, nao dado */
    const guias = el('g', { class: 'cerebro-guias' });
    for (const r of [120, 210, 285]) guias.appendChild(el('circle', { r, cx: 0, cy: 0 }));
    svg.appendChild(guias);

    const gLinhas = el('g', { class: 'cerebro-linhas' });
    const gItens = el('g', { class: 'cerebro-itens' });
    svg.appendChild(gLinhas);
    svg.appendChild(gItens);

    /* ---------------------------------------------------- centro (no) ---- */
    const centro = el('g', { class: 'cerebro-centro' });
    centro.appendChild(el('circle', { r: 78, class: 'cerebro-centro-halo' }));
    centro.appendChild(el('circle', { r: 60, class: 'cerebro-centro-disco', stroke: COR_ST[no.st] }));
    const nomeCurto = no.name.length > 30 ? no.name.slice(0, 29) + '…' : no.name;
    const linhas = nomeCurto.length > 16
      ? [nomeCurto.slice(0, nomeCurto.lastIndexOf(' ', 16) > 4 ? nomeCurto.lastIndexOf(' ', 16) : 16),
         nomeCurto.slice(nomeCurto.lastIndexOf(' ', 16) > 4 ? nomeCurto.lastIndexOf(' ', 16) + 1 : 16)]
      : [nomeCurto];
    linhas.forEach((l, i) => {
      centro.appendChild(texto(l, { class: 'cerebro-centro-nome', y: linhas.length === 1 ? 2 : i * 14 - 5, 'text-anchor': 'middle' }));
    });
    centro.appendChild(texto(ROTULO_ST[no.st], {
      class: 'cerebro-centro-st', y: linhas.length === 1 ? 20 : 26, 'text-anchor': 'middle',
    }));
    gItens.appendChild(centro);

    /* ------------------- cadeia ponta a ponta (produto/fluxo, 1Z) -------- */
    /* So para os produtos reais (IM/EM/IA/EA): a leitura transversal, com
       CADA aresta citando a relacao real da BASE. Aresta ausente = elo
       ausente — a cadeia encolhe, nunca se inventa. */
    if (ehProduto(no.external_id)) {
      const cadeia = derivarCadeiaDoProduto(nos, relacoes, no.external_id);
      const n = cadeia.length;
      if (n) {
        const passo = Math.min(190, 700 / Math.max(n, 1));
        const x0 = -((n - 1) * passo) / 2;
        cadeia.forEach((elo, i) => {
          const x = x0 + i * passo;
          const g = el('g', {
            class: 'cerebro-elo cerebro-elo-' + elo.papel,
            transform: `translate(${x} -258)`,
            tabindex: 0, role: 'button',
            'aria-label': `${elo.no.name} — ${elo.papel === 'suporte' ? 'suporte transversal' : 'etapa da cadeia'}`,
          });
          g.appendChild(el('rect', { class: 'cerebro-elo-caixa', x: -78, y: -16, width: 156, height: 34, rx: 8 }));
          const nomeElo = elo.no.name.length > 20 ? elo.no.name.slice(0, 19) + '…' : elo.no.name;
          g.appendChild(texto(nomeElo, { class: 'cerebro-elo-nome', y: 0, 'text-anchor': 'middle' }));
          g.appendChild(texto(elo.papel === 'hospeda' ? 'hospeda este fluxo' : elo.papel,
            { class: 'cerebro-elo-papel', y: 13, 'text-anchor': 'middle' }));
          const tt = el('title');
          tt.textContent = elo.no.name + (elo.relacao ? ' — relacao real: ' + elo.relacao : '');
          g.appendChild(tt);
          g.addEventListener('click', () => opcoes.aoSelecionar(elo.no.external_id));
          gItens.appendChild(g);
          if (i > 0) {
            const xa = x0 + (i - 1) * passo + 80, xb = x - 80;
            gLinhas.appendChild(el('line', {
              class: 'cerebro-elo-seta' + (elo.papel === 'suporte' ? ' cerebro-elo-seta-suporte' : ''),
              x1: xa, y1: -258, x2: xb, y2: -258,
            }));
            if (elo.relacao) {
              gItens.appendChild(texto(elo.relacao.slice(0, 24), {
                class: 'cerebro-elo-relacao', x: (xa + xb) / 2, y: -270, 'text-anchor': 'middle',
              }));
            }
          }
        });
        /* SPATIAL v5 · A CADEIA TEM DONO, e a cena diz quem e. Ela e derivada
           do PAI do produto; quando varios produtos compartilham o mesmo pai,
           e a MESMA cadeia para todos eles. Sem esta linha, a leitura radial
           sugeria "a rota deste produto" — que nao existe no dado, porque
           nenhum produto participa de relacao de fluxo. O nome sai do proprio
           elo de papel `hospeda`, nunca de texto escrito a mao. */
        const dono = hospedeiroDaCadeia(cadeia);
        if (dono) {
          gItens.appendChild(texto(`cadeia de ${dono.name} — compartilhada, nao e rota deste produto`, {
            class: 'cerebro-cadeia-dono', y: -302, 'text-anchor': 'middle',
          }));
        }
        gItens.appendChild(texto('cadeia ponta a ponta — derivada das relacoes reais da BASE', {
          class: 'cerebro-cadeia-nota', y: -288, 'text-anchor': 'middle',
        }));
      }
    }

    /* --------------------------------- orbita superior: estrutura real --- */
    /* GRAMATICA: hierarquia (filhos) mora no arco superior com linhas
       solidas; RELACOES (pontas) moram nas LATERAIS com linhas tracejadas —
       hierarquia e relacao nao sao a mesma coisa e o espaco ensina isso. */
    const filhos = nos.filter((n) => n.parent_external_id === no.external_id).sort(compararIrmaos);
    const pontas = relacoes
      .filter((r) => r.from === no.external_id || r.to === no.external_id)
      .map((r) => (r.from === no.external_id ? r.to : r.from))
      .filter((id, i, arr) => arr.indexOf(id) === i)
      .map((id) => nos.find((n) => n.external_id === id))
      .filter((n): n is FomNode => !!n && (n.parent_external_id !== no.external_id
        /* GATE Financeiro-geometria: um filho que SUPORTA o proprio contexto
           (relacao /suporte/ para este no) entra pela gramatica de RELACAO */
        || relacoes.some((r) => r.from === n.external_id && r.to === no.external_id && /suporte/.test(r.type))));
    /* FILTRO SEMANTICO (1AE): quando o CONTEXTO e organizacional (tipo real
       org/mgmt/unit/gest), filhos de tipo `process` NAO pertencem a leitura
       estrutural — objetos de auditoria e dimensoes de processo vao para
       grupos proprios, discretos e acessiveis. Quando o contexto E processo,
       filhos process sao a estrutura normal. Derivado do TIPO, nunca de
       lista de nomes. */
    const contextoOrganizacional = ['org', 'mgmt', 'unit', 'gest'].includes(no.node_type ?? '');
    /* GATE 1AE.2 — gramatica de PROCESSO (apresentacao pura sobre dado real):
       · cenaEtapas: o contexto tem filhos child_kind='flow' -> eles viram uma
         SEQUENCIA vertical na ordem do ORDINAL (dado real da BASE);
       · cenaComposicao: o contexto (process) tem >=2 filhos-processo que
         possuem fluxo -> composicao ponta a ponta: fluxo primario expandido,
         etapa de TRANSICAO (derivada de step_kind /handoff/ ou da relacao
         'relacionado a' real) entre os grupos, demais grupos como cartoes.
       A costura entre os grupos e COMPOSICAO DA APRESENTACAO — declarada na
       tela — nunca uma relacao de banco. Nenhum dado e criado ou mutado. */
    const etapasFluxo = nos
      .filter((n2) => n2.parent_external_id === no.external_id && n2.child_kind === 'flow')
      .sort(compararIrmaos);
    const gruposFluxo = (no.node_type ?? '') === 'process'
      ? filhos
          .filter((f) => (f.node_type ?? '') === 'process'
            && nos.some((n2) => n2.parent_external_id === f.external_id && n2.child_kind === 'flow'))
          .sort(compararIrmaos)
      : [];
    const cenaComposicao = gruposFluxo.length >= 2;
    const cenaEtapas = !cenaComposicao && etapasFluxo.length >= 2;
    const cenaProcessual = cenaComposicao || cenaEtapas;
    const todosFilhos = filhos
      .filter((f) => !(cenaEtapas && etapasFluxo.includes(f)) && !(cenaComposicao && gruposFluxo.includes(f)))
      .slice(0, 18);
    const filhosProcessuais = contextoOrganizacional
      ? todosFilhos.filter((f) => (f.node_type ?? '') === 'process')
      : [];
    /* GATE Financeiro-geometria: em contexto organizacional, um filho que
       SUPORTA o proprio contexto (relacao real /suporte/ para este no)
       pertence a leitura de arquitetura do NIVEL ACIMA — aqui ele aparece
       pela gramatica de RELACAO (ponta), nunca como descendente. */
    const filhosSuporteAoContexto = contextoOrganizacional
      ? todosFilhos.filter((f) => relacoes.some((r) =>
          r.from === f.external_id && r.to === no.external_id && /suporte/.test(r.type)))
      : [];
    const soFilhos = todosFilhos.filter((f) =>
      !filhosProcessuais.includes(f) && !filhosSuporteAoContexto.includes(f)).slice(0, 14);
    const soPontas = pontas.filter((pnt) =>
      !soFilhos.includes(pnt) && !filhosProcessuais.includes(pnt)
      /* fora do contexto organizacional o filho-suporte segue como filho
         normal — nao pode duplicar como ponta */
      && (contextoOrganizacional || pnt.parent_external_id !== no.external_id)).slice(0, 6);
    /* GATE Financeiro: quem se relaciona por SUPORTE a um filho-unit deste
       contexto PARTICIPA da arquitetura organizacional — em contexto
       organizacional e PROMOVIDO ao mesmo nivel dos demais blocos (posicao
       espacial = participante da estrutura; inserido ao lado do bloco com
       quem se relaciona). A relacao real segue visivel como aresta
       tracejada SECUNDARIA com o tipo verbatim. NENHUM reparent: derivacao
       de relacao ja existente. Fora do contexto organizacional, o suporte
       segue lateral (1AA.1). */
    const unitsDoContexto = soFilhos.filter((f) => (f.node_type ?? '') === 'unit');
    const idsNaCena = new Set([...soFilhos, ...soPontas].map((e2) => e2.external_id));
    const suportes = relacoes
      .filter((r) => /suporte/.test(r.type) && unitsDoContexto.some((u) => u.external_id === r.to))
      .map((r) => ({ no: nos.find((n) => n.external_id === r.from), rel: r }))
      .filter((x): x is { no: FomNode; rel: Relation } => !!x.no && !idsNaCena.has(x.no.external_id))
      .slice(0, 3);
    const promovidos = contextoOrganizacional ? suportes : [];
    const arcoFilhos = [...soFilhos];
    for (const s2 of promovidos) {
      const idx = arcoFilhos.findIndex((f) => f.external_id === s2.rel.to);
      arcoFilhos.splice(idx >= 0 ? idx + 1 : arcoFilhos.length, 0, s2.no);
    }
    const estrutura = [...arcoFilhos, ...soPontas];
    /* composicao: em contexto organizacional os blocos ficam num MESMO
       NIVEL (linha equilibrada — leitura de arquitetura); demais contextos
       mantem o arco. */
    const posArco = contextoOrganizacional && arcoFilhos.length <= 6
      ? arcoFilhos.map((_, i) => ({ x: (i - (arcoFilhos.length - 1) / 2) * 128, y: -172 }))
      : arco(arcoFilhos.length, -160, -20, 200);
    const posLados = soPontas.map((_, i) => ({
      x: (i % 2 === 0 ? -1 : 1) * 322,
      y: -140 + Math.floor(i / 2) * 66,
    }));
    /* nas cenas processuais a estrutura restante (nao-fluxo) fica na
       lateral direita — a coluna do fluxo e o protagonista */
    const posArcoOuLateral = cenaProcessual
      ? arcoFilhos.map((_, i) => ({ x: 258, y: -204 + i * 72 }))
      : posArco;
    const posEstrutura = [...posArcoOuLateral, ...posLados];
    if (arcoFilhos.length) rotuloZona(gItens as SVGGElement, 'ESTRUTURA', cenaProcessual ? 258 : 0, -238);
    if (soPontas.length) rotuloZona(gItens as SVGGElement, 'RELACOES', 322, -172);
    /* LOD anti-colisao: com muitos itens, os rotulos se ESCALONAM em tres
       raios alternados e encurtam — presenca sem sobreposicao. O nome inteiro
       continua no tooltip e no aria-label. */
    const denso = arcoFilhos.length > 8;
    estrutura.forEach((filho, i) => {
      const p = posEstrutura[i]!;
      const ehPonta = soPontas.includes(filho);
      gLinhas.appendChild(el('line', {
        class: 'cerebro-linha-estrutura' + (ehPonta ? ' cerebro-linha-relacao' : ''),
        x1: 0, y1: ehPonta ? 0 : -52, x2: p.x, y2: p.y + (ehPonta ? 0 : 14),
      }));
      /* GRAMATICA POR CLASSE (1AA.1), derivada do TIPO REAL do dominio:
           unit            -> PILAR (departamento)
           partner/network -> EXTERNO (losango — entidade de fora)
           produto (op-*)  -> CAPSULA (fluxo)
           demais          -> no circular */
      const tipoReal = filho.node_type ?? '';
      const promovido = promovidos.find((s2) => s2.no.external_id === filho.external_id);
      const ehUnit = tipoReal === 'unit' || !!promovido;
      const ehExterno = tipoReal === 'partner' || tipoReal === 'network';
      const ehProd = ehProduto(filho.external_id);
      const g = el('g', {
        class: 'cerebro-item cerebro-estrutura'
          + (ehPonta ? ' cerebro-ponta' : '')
          + (ehUnit ? ' cerebro-pilar' : '')
          + (ehExterno ? ' cerebro-externo' : '')
          + (ehProd ? ' cerebro-capsula' : ''),
        transform: `translate(${p.x} ${p.y})`,
        tabindex: 0, role: 'button',
        'aria-label': `entrar em ${filho.name}`,
      });
      if (ehUnit) {
        g.appendChild(el('rect', { class: 'cerebro-estrutura-disco cerebro-pilar-caixa', x: -30, y: -15, width: 60, height: 30, rx: 5, stroke: COR_ST[filho.st] }));
        g.appendChild(texto(
          promovido ? (ROTULO_TIPO[tipoReal as NodeType] ?? 'funcao').toLowerCase() : 'departamento',
          { class: 'cerebro-tipo-rotulo', y: 27, 'text-anchor': 'middle' }));
      } else if (ehExterno) {
        g.appendChild(el('rect', { class: 'cerebro-estrutura-disco cerebro-externo-caixa', x: -12, y: -12, width: 24, height: 24, rx: 3, transform: 'rotate(45)', stroke: COR_ST[filho.st] }));
        g.appendChild(texto('externo', { class: 'cerebro-tipo-rotulo', y: 30, 'text-anchor': 'middle' }));
      } else if (ehProd) {
        g.appendChild(el('rect', { class: 'cerebro-estrutura-disco cerebro-capsula-caixa', x: -26, y: -12, width: 52, height: 24, rx: 12, stroke: COR_ST[filho.st] }));
        g.appendChild(texto('produto', { class: 'cerebro-tipo-rotulo', y: 26, 'text-anchor': 'middle' }));
      } else {
        g.appendChild(el('circle', { r: 15, class: 'cerebro-estrutura-disco', stroke: COR_ST[filho.st] }));
      }
      const maxCh = denso ? 12 : 18;
      const rot = filho.name.length > maxCh ? filho.name.slice(0, maxCh - 1) + '…' : filho.name;
      if (ehUnit || ehProd) {
        /* pilar/capsula: o nome mora DENTRO da forma */
        const rotIn = filho.name.length > 11 ? filho.name.slice(0, 10) + '…' : filho.name;
        g.appendChild(texto(rotIn, { class: 'cerebro-rotulo cerebro-rotulo-forma', y: 3.5, 'text-anchor': 'middle' }));
      } else {
        const yRot = denso ? [-24, -38, -52][i % 3]! : -22;
        g.appendChild(texto(rot, { class: 'cerebro-rotulo' + (denso ? ' cerebro-rotulo-denso' : ''), y: yRot, 'text-anchor': 'middle' }));
      }
      const tt = el('title');
      tt.textContent = filho.name + ' — ' + ROTULO_ST[filho.st]
        + (promovido ? ' · participa da arquitetura; relacao real: ' + promovido.rel.type + ' (nao e hierarquia)' : '');
      g.appendChild(tt);
      const entrar = (): void => opcoes.aoSelecionar(filho.external_id);
      g.addEventListener('click', entrar);
      g.addEventListener('keydown', (ev) => {
        if ((ev as KeyboardEvent).key === 'Enter') entrar();
      });
      gItens.appendChild(g);
    });

    /* relacao real PRESERVADA como aresta secundaria entre os blocos —
       subordinada a estrutura, nunca criterio de hierarquia */
    for (const sup of promovidos) {
      const iDe = arcoFilhos.findIndex((f) => f.external_id === sup.no.external_id);
      const iPara = arcoFilhos.findIndex((f) => f.external_id === sup.rel.to);
      if (iDe < 0 || iPara < 0) continue;
      const pa = posArco[iDe]!;
      const pb = posArco[iPara]!;
      gLinhas.appendChild(el('path', {
        class: 'cerebro-linha-suporte',
        d: `M ${pa.x} ${pa.y - 20} C ${pa.x} ${pa.y - 46}, ${pb.x} ${pb.y - 46}, ${pb.x} ${pb.y - 20}`,
      }));
      gLinhas.appendChild(texto(sup.rel.type, {
        class: 'cerebro-relacao-rotulo',
        x: (pa.x + pb.x) / 2, y: pa.y - 44, 'text-anchor': 'middle',
      }));
    }

    /* 1AE.1: filhos processuais NAO aparecem na cena organizacional — nem
       como grupos discretos. Eles continuam no dado e acessiveis pela
       perspectiva PROCESSOS (T53/T54 garantem os dois lados). */

    /* ------------- transversal por relacao real (1AA.1): SUPORTE --------- */
    /* Um no que se relaciona por SUPORTE a um filho-unit deste contexto e
       renderizado como pilar LATERAL, com a aresta tracejada citando o tipo
       verbatim da relacao — hierarquia e suporte nao sao a mesma coisa, e o
       espaco ensina isso sem tocar o dado. */
    const suportesLaterais = contextoOrganizacional ? [] : suportes;
    suportesLaterais.forEach((sup, i) => {
      const alvoIdx = arcoFilhos.findIndex((f) => f.external_id === sup.rel.to);
      const alvoPos = posArco[alvoIdx] ?? { x: 0, y: -100 };
      const px = 330, py = -60 + i * 74;
      gLinhas.appendChild(el('line', {
        class: 'cerebro-linha-suporte',
        x1: px - 34, y1: py, x2: alvoPos.x + 30, y2: alvoPos.y + 8,
      }));
      const g = el('g', {
        class: 'cerebro-item cerebro-estrutura cerebro-pilar cerebro-suporte',
        transform: `translate(${px} ${py})`,
        tabindex: 0, role: 'button',
        'aria-label': `${sup.no.name} — ${sup.rel.type} (relacao real, nao hierarquia)`,
      });
      g.appendChild(el('rect', { class: 'cerebro-estrutura-disco cerebro-pilar-caixa', x: -30, y: -15, width: 60, height: 30, rx: 5, stroke: COR_ST[sup.no.st] }));
      const rotS = sup.no.name.length > 11 ? sup.no.name.slice(0, 10) + '…' : sup.no.name;
      g.appendChild(texto(rotS, { class: 'cerebro-rotulo', y: 3, 'text-anchor': 'middle' }));
      g.appendChild(texto(sup.rel.type.slice(0, 26), { class: 'cerebro-tipo-rotulo', y: 27, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = sup.no.name + ' — relacao real: ' + sup.rel.type + ' (transversal; nao e filho deste contexto)';
      g.appendChild(tt);
      g.addEventListener('click', () => opcoes.aoSelecionar(sup.no.external_id));
      gItens.appendChild(g);
    });
    if (suportesLaterais.length) rotuloZona(gItens as SVGGElement, 'SUPORTE · relacao real', 330, -104);

    /* =================== GATE 1AE.2 · cena de PROCESSO ==================== */
    if (cenaProcessual) {
      const FX = -240;
      /* etapas como ESTACOES sobre um trilho continuo — gramatica de
         process flow, nao coluna de cartoes nem organograma */
      const desenharEtapa = (f: FomNode, y: number): void => {
        const g = el('g', {
          class: 'cerebro-item cerebro-estrutura cerebro-etapa',
          transform: `translate(${FX} ${y})`, tabindex: 0, role: 'button',
          'aria-label': `entrar em ${f.name} — etapa do fluxo`,
        });
        g.appendChild(el('circle', { class: 'cerebro-etapa-estacao', r: 8.5, stroke: COR_ST[f.st] }));
        const rot = f.name.length > 26 ? f.name.slice(0, 25) + '…' : f.name;
        g.appendChild(texto(rot, { class: 'cerebro-rotulo cerebro-etapa-nome', x: 18, y: 3.5 }));
        const tt = el('title');
        tt.textContent = f.name + ' — ' + ROTULO_ST[f.st] + ' · etapa do fluxo (ordem real do dado)';
        g.appendChild(tt);
        g.addEventListener('click', () => opcoes.aoSelecionar(f.external_id));
        g.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') opcoes.aoSelecionar(f.external_id); });
        gItens.appendChild(g);
      };
      const setaFluxo = (yDe: number, yAte: number): void => {
        gLinhas.appendChild(el('line', { class: 'cerebro-fluxo-seta', x1: FX, y1: yDe, x2: FX, y2: yAte }));
        gLinhas.appendChild(el('path', { class: 'cerebro-fluxo-ponta', d: `M ${FX - 4} ${yAte - 5} L ${FX} ${yAte} L ${FX + 4} ${yAte - 5}` }));
      };
      /* relacoes REAIS de cada etapa: ator externo que aponta para a etapa
         (ex.: Cliente —aprova→ Aprovacao) e anotacao do que a etapa aponta
         (ex.: Geracao —gera→ CASO). Nada e desenhado sem aresta real. */
      const anotarRelacoes = (f: FomNode, y: number): void => {
        for (const r of relacoes) {
          if (r.to === f.external_id && r.from !== no.external_id) {
            const ator = nos.find((n2) => n2.external_id === r.from);
            if (!ator) continue;
            gLinhas.appendChild(el('line', { class: 'cerebro-linha-suporte', x1: -322, y1: y, x2: FX - 12, y2: y }));
            const ga = el('g', {
              class: 'cerebro-item cerebro-ator',
              transform: `translate(-334 ${y})`, tabindex: 0, role: 'button',
              'aria-label': `${ator.name} — ${r.type} (relacao real)`,
            });
            ga.appendChild(el('rect', { class: 'cerebro-externo-caixa', x: -9, y: -9, width: 18, height: 18, rx: 3, transform: 'rotate(45)', stroke: COR_ST[ator.st] }));
            ga.appendChild(texto(ator.name.length > 13 ? ator.name.slice(0, 12) + '…' : ator.name, { class: 'cerebro-rotulo cerebro-rotulo-denso', y: -16, 'text-anchor': 'middle' }));
            const ta = el('title');
            ta.textContent = ator.name + ' — relacao real: ' + r.type;
            ga.appendChild(ta);
            ga.addEventListener('click', () => opcoes.aoSelecionar(ator.external_id));
            gItens.appendChild(ga);
            gItens.appendChild(texto(r.type.slice(0, 14), { class: 'cerebro-relacao-rotulo', x: (-322 + FX - 12) / 2, y: y - 5, 'text-anchor': 'middle' }));
          }
          if (r.from === f.external_id) {
            const alvo = nos.find((n2) => n2.external_id === r.to);
            if (!alvo) continue;
            const rotAlvo = alvo.name.length > 14 ? alvo.name.slice(0, 13) + '…' : alvo.name;
            gItens.appendChild(texto(`${r.type} → ${rotAlvo}`.slice(0, 32), { class: 'cerebro-anotacao', x: 18, y: 14, transform: `translate(${FX} ${y})` }));
          }
        }
      };

      if (cenaEtapas) {
        rotuloZona(gItens as SVGGElement, ((no.flow_title ?? 'FLUXO') + ' · ORDEM REAL').toUpperCase().slice(0, 44), FX, -262);
        const passo = etapasFluxo.length > 9 ? 30 : 36;
        etapasFluxo.slice(0, 12).forEach((f, i) => {
          const y = -238 + i * passo;
          if (i) setaFluxo(y - passo + 11, y - 11);
          desenharEtapa(f, y);
          anotarRelacoes(f, y);
        });
      }

      if (cenaComposicao) {
        const transicao = gruposFluxo.find((g2) =>
          nos.some((n2) => n2.parent_external_id === g2.external_id && /handoff/i.test(n2.step_kind ?? ''))
          || relacoes.some((r) => r.from === g2.external_id && /relacionado/.test(r.type)));
        const normais = gruposFluxo.filter((g2) => g2 !== transicao);
        const primario = normais[0];
        const demais = normais.slice(1);
        let y = -238;
        if (primario) {
          rotuloZona(gItens as SVGGElement, (primario.name + ' · FLUXO REAL').toUpperCase().slice(0, 44), FX, -262);
          const passos = nos
            .filter((n2) => n2.parent_external_id === primario.external_id && n2.child_kind === 'flow')
            .sort(compararIrmaos).slice(0, 10);
          passos.forEach((f, i) => {
            const yy = -238 + i * 31;
            if (i) setaFluxo(yy - 31 + 11, yy - 11);
            desenharEtapa(f, yy);
            anotarRelacoes(f, yy);
            y = yy;
          });
        }
        y += 34;
        if (transicao) {
          /* faixa de TRANSICAO: gramatica PROPRIA — dupla linha tracejada,
             declarada como composicao. NAO e a seta de fluxo, NAO e aresta */
          gLinhas.appendChild(el('line', { class: 'cerebro-transicao-linha', x1: FX - 62, y1: y - 8, x2: FX + 62, y2: y - 8 }));
          gLinhas.appendChild(el('line', { class: 'cerebro-transicao-linha', x1: FX - 62, y1: y - 4, x2: FX + 62, y2: y - 4 }));
          gItens.appendChild(texto('TRANSICAO', { class: 'cerebro-zona', x: FX, y: y + 8, 'text-anchor': 'middle' }));
          gItens.appendChild(texto('composicao da apresentacao · elo etapa-a-etapa nao comprovado', { class: 'cerebro-anotacao', x: FX, y: y + 19, 'text-anchor': 'middle' }));
          y += 36;
          const nPassos = nos.filter((n2) => n2.parent_external_id === transicao.external_id && n2.child_kind === 'flow').length;
          const gT = el('g', {
            class: 'cerebro-item cerebro-estrutura cerebro-transicao-cartao',
            transform: `translate(${FX} ${y})`, tabindex: 0, role: 'button',
            'aria-label': `entrar em ${transicao.name} — etapa de transicao do fluxo`,
          });
          gT.appendChild(el('rect', { class: 'cerebro-etapa-caixa cerebro-transicao-caixa', x: -100, y: -16, width: 200, height: 32, rx: 9, stroke: COR_ST[transicao.st] }));
          gT.appendChild(texto(transicao.name.length > 26 ? transicao.name.slice(0, 25) + '…' : transicao.name, { class: 'cerebro-rotulo cerebro-rotulo-forma', y: -1, 'text-anchor': 'middle' }));
          gT.appendChild(texto(`etapa do fluxo · ${nPassos} passos`, { class: 'cerebro-tipo-rotulo', y: 11, 'text-anchor': 'middle' }));
          const alvoRel = relacoes.find((r) => r.from === transicao.external_id && /relacionado/.test(r.type));
          const alvoNo = alvoRel ? nos.find((n2) => n2.external_id === alvoRel.to) : null;
          const tT = el('title');
          tT.textContent = transicao.name + ' — etapa de transicao'
            + (alvoNo ? ' · relacionado a ' + alvoNo.name + ' (anatomia auditada)' : '');
          gT.appendChild(tT);
          gT.addEventListener('click', () => opcoes.aoSelecionar(transicao.external_id));
          gT.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') opcoes.aoSelecionar(transicao.external_id); });
          gItens.appendChild(gT);
          y += 46;
        }
        for (const g2 of demais) {
          gLinhas.appendChild(el('line', { class: 'cerebro-composicao-seta', x1: FX, y1: y - 26, x2: FX, y2: y - 18 }));
          const nEt = nos.filter((n2) => n2.parent_external_id === g2.external_id && n2.child_kind === 'flow').length;
          const gD = el('g', {
            class: 'cerebro-item cerebro-estrutura cerebro-grupo-fluxo',
            transform: `translate(${FX} ${y})`, tabindex: 0, role: 'button',
            'aria-label': `entrar em ${g2.name} — ${nEt} etapas`,
          });
          gD.appendChild(el('rect', { class: 'cerebro-etapa-caixa', x: -100, y: -16, width: 200, height: 32, rx: 9, stroke: COR_ST[g2.st] }));
          gD.appendChild(texto(g2.name.length > 26 ? g2.name.slice(0, 25) + '…' : g2.name, { class: 'cerebro-rotulo cerebro-rotulo-forma', y: -1, 'text-anchor': 'middle' }));
          gD.appendChild(texto(`${nEt} etapas`, { class: 'cerebro-tipo-rotulo', y: 11, 'text-anchor': 'middle' }));
          gD.addEventListener('click', () => opcoes.aoSelecionar(g2.external_id));
          gD.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') opcoes.aoSelecionar(g2.external_id); });
          gItens.appendChild(gD);
          y += 58;
        }
        /* proveniencia REAL da transicao (nivel departamento) — derivada das
           arestas existentes, nunca inventada; abaixo da composicao inteira */
        const alvoRel2 = transicao ? relacoes.find((r) => r.from === transicao.external_id && /relacionado/.test(r.type)) : null;
        const alvoNo2 = alvoRel2 ? nos.find((n2) => n2.external_id === alvoRel2.to) : null;
        if (alvoNo2) {
          const entra = relacoes.find((r) => r.to === alvoNo2.external_id && /envia/.test(r.type));
          const sai = relacoes.find((r) => r.from === alvoNo2.external_id && /recebe/.test(r.type));
          const de = entra ? nos.find((n2) => n2.external_id === entra.from) : null;
          const para = sai ? nos.find((n2) => n2.external_id === sai.to) : null;
          if (de && para) {
            const meio = alvoNo2.name.length > 20 ? alvoNo2.name.slice(0, 19) + '…' : alvoNo2.name;
            gItens.appendChild(texto(
              `relacao real: ${de.name} —envia para→ ${meio} —recebe→ ${para.name}`,
              { class: 'cerebro-proveniencia', x: FX - 100, y: y - 20, 'text-anchor': 'start' }));
          }
        }
      }
    }

    /* -------------------------- hemisferio inferior: nucleo de saber ----- */
    /* ordenado por CLASSE epistemica: o observado (E1) no miolo, o
       documentado (E2) em volta, relatos depois — a massa ensina a diferenca */
    const ORDEM_ST: Record<string, number> = { E1: 0, E2: 1, E3: 2 };
    const know = [...no.know].sort((k1, k2) =>
      (ORDEM_ST[k1.st] ?? 9) - (ORDEM_ST[k2.st] ?? 9));
    const gaps = no.gaps;
    const doNoInvs = invs.filter((i) => i.node_external_id === no.external_id);

    /* NUCLEO DE CONHECIMENTO: a massa das referencias — aglomerado
       phyllotaxis onde CADA particula e um conhecimento real, com o nivel
       escrito. Linhas tracejadas convergem do contexto ao nucleo. O raio do
       aglomerado cresce com o dado: assimetria visivel, nunca decoracao. */
    const nucleo = aglomerado(know.length);
    const NUCLEO_Y = 168;
    if (know.length) {
      gItens.appendChild(el('circle', {
        class: 'cerebro-nucleo-borda', cx: 0, cy: NUCLEO_Y, r: nucleo.raio,
      }));
      const conv = Math.min(know.length, 7);
      for (let i = 0; i < conv; i++) {
        const t = conv === 1 ? 0.5 : i / (conv - 1);
        const ang = (-150 + 120 * t) * (Math.PI / 180);
        gLinhas.appendChild(el('line', {
          class: 'cerebro-linha-know',
          x1: 0, y1: 66,
          x2: Math.sin(ang) * nucleo.raio * 0.7,
          y2: NUCLEO_Y - Math.cos(ang) * nucleo.raio * 0.35 - nucleo.raio * 0.55,
        }));
      }
      rotuloZona(gItens as SVGGElement, 'CONHECIMENTO · ' + know.length, 0, NUCLEO_Y + nucleo.raio + 18);
      /* quando o contexto tem OS DOIS lados, a massa declara o eixo central
         do FOM: observado × documentado (contagens reais) */
      const nE1 = know.filter((k) => k.st === 'E1').length;
      const nE2 = know.filter((k) => k.st === 'E2').length;
      if (nE1 > 0 && nE2 > 0) {
        gItens.appendChild(texto(`observado ${nE1} × documentado ${nE2}`, {
          class: 'cerebro-eixo-epist', x: 0, y: NUCLEO_Y + nucleo.raio + 32, 'text-anchor': 'middle',
        }));
      }
    }

    /* divergencia DECLARADA no guia deste contexto: aparece no cerebro como
       marcador ambar com [INVESTIGAR] — pergunta, nunca veredito */
    const divergencias = guiaDe(no.external_id)?.divergencias ?? [];
    divergencias.slice(0, 2).forEach((div, i) => {
      const g = el('g', {
        class: 'cerebro-item cerebro-divergencia',
        transform: `translate(${-(nucleo.raio + 118)} ${NUCLEO_Y - 96 - i * 54})`,
        tabindex: 0, role: 'button',
        'aria-label': 'divergencia documentado × observado — merece investigacao',
      });
      g.appendChild(el('rect', { class: 'cerebro-div-caixa', x: -66, y: -15, width: 132, height: 32, rx: 7 }));
      g.appendChild(texto('DOCUMENTADO × OBSERVADO', { class: 'cerebro-div-titulo', y: -3, 'text-anchor': 'middle' }));
      g.appendChild(texto('divergencia · investigar', { class: 'cerebro-div-sub', y: 10, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = div.documentado.t.slice(0, 90) + ' × ' + div.observado.t.slice(0, 90) + ' — clique para investigar';
      g.appendChild(tt);
      g.addEventListener('click', () => opcoes.aoInvestigarLacuna?.(no, div.pergunta));
      gItens.appendChild(g);
    });
    know.forEach((k, i) => {
      const p = pontoAglomerado(i);
      const g = el('g', {
        class: 'cerebro-item cerebro-know',
        transform: `translate(${p.x} ${NUCLEO_Y + p.y})`,
        tabindex: 0, role: 'button',
        'aria-label': `conhecimento ${k.st}: ${k.text.slice(0, 80)}`,
      });
      g.appendChild(el('circle', { r: 10, class: 'cerebro-know-disco', fill: COR_ST[k.st] }));
      g.appendChild(texto(k.st, { class: 'cerebro-know-st', y: 3.5, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = `${ROTULO_ST[k.st]} — ${k.text}`;
      g.appendChild(tt);
      /* progressive disclosure (1AC item 7): a particula abre um INSPETOR do
         PROPRIO conhecimento — nivel, texto, origem, fonte — e so entao o
         usuario decide aprofundar na aba/evidencia */
      g.addEventListener('click', () => abrirInspetor(k));
      gItens.appendChild(g);
    });

    /* LACUNAS: enxame proprio de aneis abertos, zona a esquerda do nucleo —
       o que NAO sabemos tem lugar e nome no espaco */
    const zonaGapX = -(nucleo.raio + 118);
    if (gaps.length) {
      rotuloZona(gItens as SVGGElement, 'LACUNAS · ' + gaps.length, zonaGapX, NUCLEO_Y + 13 * Math.sqrt(gaps.length) + 32);
    }
    gaps.forEach((gap, i) => {
      const pa = pontoAglomerado(i, 15);
      const p = { x: zonaGapX + pa.x, y: NUCLEO_Y + pa.y };
      gLinhas.appendChild(el('line', { class: 'cerebro-linha-gap', x1: -34, y1: 52, x2: p.x + 8, y2: p.y - 10 }));
      const g = el('g', {
        class: 'cerebro-item cerebro-gap', transform: `translate(${p.x} ${p.y})`,
        tabindex: 0, role: 'button',
        'aria-label': `lacuna: ${gap.slice(0, 80)}`,
      });
      g.appendChild(el('circle', { r: 11, class: 'cerebro-gap-anel' }));
      g.appendChild(texto('?', { class: 'cerebro-gap-q', y: 4, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = 'lacuna — ' + gap;
      g.appendChild(tt);
      g.addEventListener('click', () => opcoes.aoAbrirAba('lacunas'));
      gItens.appendChild(g);
    });

    /* Investigacoes ABERTAS sao perguntas de agora — cada uma aparece.
       ENCERRADAS sao historia: continuam presentes, mas AGREGADAS num unico
       marcador discreto com a contagem — "N investigacoes" nunca pode
       ler-se como "N problemas atuais". */
    const invsAbertas = doNoInvs.filter((i) => i.status !== 'encerrada');
    const invsEncerradas = doNoInvs.filter((i) => i.status === 'encerrada');
    const zonaInvX = nucleo.raio + 118;
    if (invsAbertas.length) {
      rotuloZona(gItens as SVGGElement, 'INVESTIGACOES · ' + invsAbertas.length, zonaInvX, NUCLEO_Y + 15 * Math.sqrt(invsAbertas.length) + 34);
    }
    invsAbertas.forEach((inv, i) => {
      const pa = pontoAglomerado(i, 17);
      const p = { x: zonaInvX + pa.x, y: NUCLEO_Y + pa.y };
      gLinhas.appendChild(el('line', { class: 'cerebro-linha-inv', x1: 34, y1: 52, x2: p.x - 8, y2: p.y - 10 }));
      const g = el('g', {
        class: 'cerebro-item cerebro-inv',
        transform: `translate(${p.x} ${p.y})`,
        tabindex: 0, role: 'button',
        'aria-label': `investigacao aberta ${inv.code}: ${inv.question.slice(0, 70)}`,
      });
      g.appendChild(el('circle', { r: 13, class: 'cerebro-inv-anel-ext' }));
      g.appendChild(el('circle', { r: 8, class: 'cerebro-inv-anel-int' }));
      g.appendChild(texto(inv.code.replace('INV-', ''), { class: 'cerebro-rotulo', y: 28, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = `${inv.code} · aberta — ${inv.question}`;
      g.appendChild(tt);
      g.addEventListener('click', () => opcoes.aoAbrirAba('investigacoes'));
      gItens.appendChild(g);
    });
    if (invsEncerradas.length) {
      const g = el('g', {
        class: 'cerebro-item cerebro-inv-hist',
        transform: `translate(${nucleo.raio + 118} ${NUCLEO_Y + 15 * Math.sqrt(Math.max(invsAbertas.length, 1)) + 74})`,
        tabindex: 0, role: 'button',
        'aria-label': `${invsEncerradas.length} investigacoes encerradas (historico)`,
      });
      g.appendChild(el('circle', { r: 14, class: 'cerebro-inv-hist-disco' }));
      g.appendChild(texto(String(invsEncerradas.length), { class: 'cerebro-inv-hist-n', y: 4, 'text-anchor': 'middle' }));
      g.appendChild(texto('encerradas', { class: 'cerebro-rotulo cerebro-rotulo-hist', y: 30, 'text-anchor': 'middle' }));
      const tt = el('title');
      tt.textContent = invsEncerradas.length + ' investigacoes encerradas — historico preservado; clique para ver';
      g.appendChild(tt);
      g.addEventListener('click', () => opcoes.aoAbrirAba('investigacoes'));
      gItens.appendChild(g);
    }

    /* --------- a AREA sabe mais que o no: agregacao real da subarvore ----- */
    /* No nivel de gestao/departamento, "o que sabemos sobre essa area" inclui
       os descendentes. Cada ponto e um conhecimento REAL de um no da
       subarvore (tooltip diz o dono; clique NAVEGA ate ele). Derivacao de
       parentesco existente — nada agregado ficticio. */
    const filhosDe = new Map<string, FomNode[]>();
    for (const n2 of nos) {
      if (!n2.parent_external_id) continue;
      const arr = filhosDe.get(n2.parent_external_id) ?? [];
      arr.push(n2);
      filhosDe.set(n2.parent_external_id, arr);
    }
    /* percurso em ordem do dominio, nao do array: os pontos de conhecimento
       da subarvore ocupam sempre o mesmo lugar (FOM-ARQ-0049) */
    for (const arr of filhosDe.values()) arr.sort(compararIrmaos);
    const descendentes: FomNode[] = [];
    const fila = [...(filhosDe.get(no.external_id) ?? [])];
    let guardaSub = 0;
    while (fila.length && guardaSub++ < 500) {
      const n2 = fila.shift()!;
      /* 1AE.1: na leitura ORGANIZACIONAL, subarvores de tipo `process` ficam
         inteiras fora da cena — inclusive sua massa de conhecimento (que
         pertence a leitura de PROCESSOS). O agregado mostrado segue o que a
         cena representa (regra dos contadores da 1X.1). */
      if (contextoOrganizacional && (n2.node_type ?? '') === 'process') continue;
      descendentes.push(n2);
      fila.push(...(filhosDe.get(n2.external_id) ?? []));
    }
    const knowsSub = descendentes.flatMap((d) =>
      d.know.map((k) => ({ dono: d, k })));
    const gapsSub = descendentes.reduce((soma, d) => soma + d.gaps.length, 0);
    if (knowsSub.length) {
      const RAIO_SUB = nucleo.raio + 58;
      knowsSub.slice(0, 80).forEach((item, i) => {
        const t = knowsSub.length === 1 ? 0.5 : i / (Math.min(knowsSub.length, 80) - 1);
        const ang = ((30 + 120 * t) * Math.PI) / 180;
        const px = Math.cos(ang) * RAIO_SUB * 1.9 - RAIO_SUB * 0.95;
        const py = NUCLEO_Y + Math.sin(ang) * (RAIO_SUB * 0.62);
        const g = el('g', {
          class: 'cerebro-item cerebro-know-sub', transform: `translate(${px} ${py})`,
          tabindex: 0, role: 'button',
          'aria-label': `conhecimento de ${item.dono.name}: ${item.k.text.slice(0, 60)}`,
        });
        g.appendChild(el('circle', { r: 5, class: 'cerebro-know-sub-ponto', fill: COR_ST[item.k.st] }));
        const tt = el('title');
        tt.textContent = item.dono.name + ' — ' + ROTULO_ST[item.k.st] + ': ' + item.k.text;
        g.appendChild(tt);
        g.addEventListener('click', () => opcoes.aoSelecionar(item.dono.external_id));
        gItens.appendChild(g);
      });
      rotuloZona(gItens as SVGGElement, `NA SUBARVORE · ${knowsSub.length} conhecimentos · ${gapsSub} lacunas`, 0, NUCLEO_Y + nucleo.raio + (know.length ? 50 : 18));
    }

    /* estado vazio honesto — a ausencia e informacao */
    if (!know.length && !gaps.length && !doNoInvs.length && !knowsSub.length) {
      svg.appendChild(texto('Este contexto ainda nao possui conhecimento consolidado.', {
        class: 'cerebro-vazio', y: 160, 'text-anchor': 'middle',
      }));
      svg.appendChild(texto('Conhecimento aparece quando uma evidencia e registrada ou uma investigacao e encerrada.', {
        class: 'cerebro-vazio cerebro-vazio-como', y: 182, 'text-anchor': 'middle',
      }));
    }

    /* entrada cinematica (1AD.2): indice de cascata pela ordem da cena —
       o CSS revela em stagger curto; reduced-motion anula tudo */
    let ordemEntrada = 0;
    for (const item of gItens.querySelectorAll('.cerebro-item, .cerebro-centro')) {
      (item as SVGElement).style.setProperty('--i', String(ordemEntrada++));
    }
    raiz.appendChild(svg);

    /* ------------------------ inspetor de conhecimento ------------------- */
    /* CONTEXTO -> CONHECIMENTO -> EVIDENCIA: um clique mostra O ITEM (nivel,
       texto, origem, fonte declarada) — nunca uma parede de texto. */
    const inspetor = document.createElement('div');
    inspetor.className = 'cerebro-inspetor';
    inspetor.hidden = true;
    raiz.appendChild(inspetor);
    function abrirInspetor(k: FomNode['know'][number]): void {
      inspetor.textContent = '';
      const nivel = document.createElement('span');
      nivel.className = 'inspetor-nivel';
      nivel.textContent = ROTULO_ST[k.st];
      nivel.style.setProperty('--cor', COR_ST[k.st]);
      inspetor.appendChild(nivel);
      const txt = document.createElement('p');
      txt.className = 'inspetor-texto';
      txt.textContent = k.text;
      inspetor.appendChild(txt);
      const meta = document.createElement('p');
      meta.className = 'inspetor-meta';
      meta.textContent = 'origem: ' + (k.origin === 'user' ? 'investigado (USER)' : 'BASE consolidada')
        + (no.src ? ' · fonte declarada do no: ' + no.src : '');
      inspetor.appendChild(meta);
      const acoes = document.createElement('div');
      acoes.className = 'inspetor-acoes';
      const abrir = document.createElement('button');
      abrir.className = 'cerebro-voltar';
      abrir.textContent = 'ver todos no painel';
      abrir.addEventListener('click', () => opcoes.aoAbrirAba('conhecimento'));
      acoes.appendChild(abrir);
      const fechar = document.createElement('button');
      fechar.className = 'cerebro-voltar';
      fechar.textContent = 'fechar';
      fechar.addEventListener('click', () => { inspetor.hidden = true; });
      acoes.appendChild(fechar);
      inspetor.appendChild(acoes);
      inspetor.hidden = false;
    }

    /* ------------------------------------------------ legenda (contada) -- */
    const legenda = document.createElement('div');
    legenda.className = 'cerebro-legenda';
    legenda.setAttribute('aria-label', 'legenda da rede de conhecimento');
    const porNivel = new Map<string, number>();
    for (const k of know) porNivel.set(k.st, (porNivel.get(k.st) ?? 0) + 1);
    const linha = (classe: string, rotulo: string, n: number): void => {
      if (!n) return;
      const li = document.createElement('div');
      li.className = 'cerebro-legenda-item';
      li.innerHTML = `<span class="cerebro-legenda-ponto ${classe}"></span>`
        + `<span>${rotulo}</span><b>${n}</b>`;
      legenda.appendChild(li);
    };
    linha('lp-estrutura', 'estrutura ligada', estrutura.length);
    for (const [st, n] of [...porNivel.entries()].sort()) {
      linha('lp-know', ROTULO_ST[st as keyof typeof ROTULO_ST] ?? st, n);
    }
    linha('lp-gap', 'lacunas — perguntas abertas', gaps.length);
    linha('lp-inv', 'investigacoes abertas', invsAbertas.length);
    linha('lp-inv-hist', 'encerradas (historico)', invsEncerradas.length);
    if (!legenda.children.length) {
      const li = document.createElement('div');
      li.className = 'cerebro-legenda-item';
      li.textContent = 'contexto sem itens — selecione outro no ou volte ao mapa';
      legenda.appendChild(li);
    }
    raiz.appendChild(legenda);

    /* ------------------------------- camada probatoria (sob demanda) ----- */
    const evid = document.createElement('div');
    evid.className = 'cerebro-evidencias';
    evid.hidden = true;
    const addSecao = (titulo: string, itens: string[]): void => {
      if (!itens.length) return;
      const h = document.createElement('h4');
      h.className = 'evid-titulo';
      h.textContent = titulo + ' · ' + itens.length;
      evid.appendChild(h);
      const ul = document.createElement('ul');
      ul.className = 'evid-lista';
      for (const t of itens) {
        const li = document.createElement('li');
        li.textContent = t.length > 110 ? t.slice(0, 109) + '…' : t;
        li.title = t;
        ul.appendChild(li);
      }
      evid.appendChild(ul);
    };
    const cab = document.createElement('h3');
    cab.className = 'evid-cabecalho';
    cab.textContent = 'Camada probatoria — ' + no.name;
    evid.appendChild(cab);
    const knowsDe = (st: string): string[] => know.filter((k) => k.st === st).map((k) => k.text);
    if (guiaDe(no.external_id)) {
      addSecao('Documentos que sustentam o guia', FONTES_GUIA.map((f) => f.fonte + ' — ' + f.t));
    }
    if (no.src) addSecao('Fonte declarada do no', [no.src]);
    addSecao('E1 · observado (evidencia da pratica)', knowsDe('E1'));
    addSecao('E2 · documentado (instrucao escrita)', knowsDe('E2'));
    addSecao('E3 · relato do operador', knowsDe('E3'));
    addSecao('Outros niveis', know.filter((k) => !['E1', 'E2', 'E3'].includes(k.st)).map((k) => k.st + ' — ' + k.text));
    addSecao('Lacunas · definicao ausente', gaps.map((g2) => String(g2)));
    addSecao('Investigacoes', doNoInvs.map((i) => i.code + ' · ' + i.status + ' — ' + i.question));
    if (evid.children.length <= 1) {
      const vazioEl = document.createElement('p');
      vazioEl.className = 'evid-vazio';
      vazioEl.textContent = 'Nao ha evidencia suficiente mapeada para este contexto.';
      evid.appendChild(vazioEl);
    }
    raiz.appendChild(evid);
    btnEvid.addEventListener('click', () => {
      evid.hidden = !evid.hidden;
      btnEvid.setAttribute('aria-pressed', String(!evid.hidden));
      btnEvid.classList.toggle('evid-ativo', !evid.hidden);
    });
  }

  return {
    render,
    mostrar(): void { raiz.hidden = false; },
    esconder(): void { raiz.hidden = true; },
    get visivel(): boolean { return !raiz.hidden; },
    /* a camada probatoria controlada de fora (grupo PERSPECTIVA, 1AD.1) */
    alternarEvidencias(): boolean {
      const evid = raiz.querySelector('.cerebro-evidencias') as HTMLElement | null;
      if (!evid) return false;
      evid.hidden = !evid.hidden;
      const btn = raiz.querySelector('.cerebro-evid-botao');
      btn?.setAttribute('aria-pressed', String(!evid.hidden));
      btn?.classList.toggle('evid-ativo', !evid.hidden);
      return !evid.hidden;
    },
    fecharEvidencias(): void {
      const evid = raiz.querySelector('.cerebro-evidencias') as HTMLElement | null;
      if (evid && !evid.hidden) {
        evid.hidden = true;
        raiz.querySelector('.cerebro-evid-botao')?.classList.remove('evid-ativo');
      }
    },
    get evidenciasVisiveis(): boolean {
      const evid = raiz.querySelector('.cerebro-evidencias') as HTMLElement | null;
      return !!evid && !evid.hidden;
    },
  };
}

export type Cerebro = ReturnType<typeof montarCerebro>;
